import React, { useState, useEffect } from "react";
import { RiCheckDoubleFill } from "react-icons/ri";
import { FaEnvelope } from "react-icons/fa";
import Button from "./../../components/Button/Button";
import ButtonDelete from "./../../components/Button/ButtonDelete";
import ButtonEyeNoRead from "../../components/Button/ButtonEyeNoRead";
import ButtonEyeRead from "../../components/Button/ButtonEyeRead";
import ButtonReply from "../../components/Button/ButtonReply";
import {
  getMessagesByUser,
  deleteMessageById,
  markMessageAsRead,
  markAllMessagesAsRead,
} from "./../../services/messageServices";
import { useMessage } from "./../../contexts/MessageContext";
import CreateResponseMessageForm from "./CreateResponseMessageForm";
import { toast } from "react-toastify";

const MessagesReceived = ({ userId, refresh, setRefresh }) => {
  const [messages, setMessages] = useState([]);
  const { unreadMessageCount, setUnreadMessageCount } = useMessage();
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(null);

  const loadMessages = async () => {
    try {
      const data = await getMessagesByUser(userId);
      const filteredMessages = (data || [])
        .filter((message) => !message.is_copy)
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      setMessages(filteredMessages);
    } catch (error) {
      console.error("Erreur lors du chargement des messages:", error);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [userId]);

  useEffect(() => {
    if (refresh) {
      loadMessages();
      setRefresh(false);
    }
  }, [refresh, setRefresh]);

  const handleReply = (message) => {
    setCurrentMessage({
      recipientId: message.sender_id,
      subject: `RE: ${message.subject}`,
    });
    setShowReplyModal(true);
  };

  const closeReplyModal = () => {
    setShowReplyModal(false);
    setCurrentMessage(null);
  };

  const promptDeleteMessage = (messageId) => {
    const confirmDelete = window.confirm(
      "Êtes-vous sûr de vouloir supprimer ce message ?"
    );
    if (confirmDelete) {
      handleDeleteMessage(messageId);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessageById(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      toast.success("Message supprimé avec succès.");
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  const toggleReadStatus = async (messageId) => {
    try {
      const message = messages.find((m) => m.id === messageId);
      if (!message) return;
      await markMessageAsRead(messageId, !message.is_read);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, is_read: !m.is_read } : m
        )
      );
      setUnreadMessageCount((current) =>
        message.is_read ? current + 1 : Math.max(0, current - 1)
      );
    } catch (error) {
      console.error("Erreur lors du basculement de l'état de lecture:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllMessagesAsRead(userId);
      setMessages((prev) => prev.map((m) => ({ ...m, is_read: true })));
      setUnreadMessageCount(0);
    } catch (error) {
      console.error(
        "Erreur lors du marquage de tous les messages comme lus:",
        error
      );
    }
  };

  return (
    <div className="w-full font-montserrat">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <FaEnvelope
              className="text-xl text-primary"
              title="Messages reçus"
            />
            {unreadMessageCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5">
                {unreadMessageCount}
              </span>
            )}
          </div>
          <h3 className="text-sm sm:text-base font-semibold">
            Messages reçus
          </h3>
        </div>
        {messages.length > 0 && (
          <Button
            onClick={handleMarkAllAsRead}
            icon={RiCheckDoubleFill}
            className="gap-2 text-xs sm:text-sm"
            hoverLabel="Tout marquer comme lu"
            text="Tout lire"
          />
        )}
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-4 text-sm bg-slate-50 dark:bg-slate-900/80 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          Aucun message reçu.
        </div>
      ) : (
        <>
          {/* Desktop : tableau */}
          <div className="hidden sm:block">
            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-200 dark:bg-slate-800">
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Date
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Détails
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((message) => (
                    <tr
                      key={message.id}
                      className={`border-t border-slate-200 dark:border-slate-700 ${
                        message.is_read
                          ? "bg-white dark:bg-slate-900"
                          : "bg-primary/5 dark:bg-primary/15"
                      }`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700 dark:text-slate-200">
                        {new Date(message.created_at).toLocaleDateString(
                          "fr-FR"
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-200">
                        {message.Sender && (
                          <>
                            <div className="font-semibold">
                              {message.Sender.nom} {message.Sender.prenom}
                            </div>
                            <div className="text-xs text-primary font-bold">
                              {message.subject}
                            </div>
                            <div
                              className="text-xs mt-1 line-clamp-3"
                              dangerouslySetInnerHTML={{
                                __html: message.content,
                              }}
                            />
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex justify-end gap-1.5">
                          <ButtonReply
                            onClick={() => handleReply(message)}
                            hoverLabel="Répondre"
                          />
                          {message.is_read ? (
                            <ButtonEyeRead
                              onClick={() => toggleReadStatus(message.id)}
                              hoverLabel="Marquer comme non lu"
                            />
                          ) : (
                            <ButtonEyeNoRead
                              onClick={() => toggleReadStatus(message.id)}
                              hoverLabel="Marquer comme lu"
                            />
                          )}
                          <ButtonDelete
                            onClick={() => promptDeleteMessage(message.id)}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile : cartes */}
          <div className="block sm:hidden space-y-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`rounded-xl border border-slate-200 dark:border-slate-700 p-3 shadow-sm ${
                  message.is_read
                    ? "bg-white dark:bg-slate-900"
                    : "bg-primary/5 dark:bg-primary/15"
                }`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    {new Date(message.created_at).toLocaleDateString("fr-FR")}
                  </span>
                  <div className="flex gap-1.5">
                    <ButtonReply
                      onClick={() => handleReply(message)}
                      hoverLabel="Répondre"
                    />
                    {message.is_read ? (
                      <ButtonEyeRead
                        onClick={() => toggleReadStatus(message.id)}
                        hoverLabel="Marquer comme non lu"
                      />
                    ) : (
                      <ButtonEyeNoRead
                        onClick={() => toggleReadStatus(message.id)}
                        hoverLabel="Marquer comme lu"
                      />
                    )}
                    <ButtonDelete
                      onClick={() => promptDeleteMessage(message.id)}
                      hoverLabel="Supprimer"
                    />
                  </div>
                </div>
                {message.Sender && (
                  <div className="text-xs text-slate-700 dark:text-slate-200">
                    <div className="font-semibold">
                      {message.Sender.nom} {message.Sender.prenom}
                    </div>
                    <div className="text-xs text-primary font-bold">
                      {message.subject}
                    </div>
                    <div
                      className="mt-1"
                      dangerouslySetInnerHTML={{
                        __html: message.content,
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {showReplyModal && currentMessage && (
        <div className="mt-4">
          <CreateResponseMessageForm
            show={showReplyModal}
            onHide={closeReplyModal}
            userId={userId}
            initialRecipientId={currentMessage.recipientId}
            initialSubject={currentMessage.subject}
          />
        </div>
      )}
    </div>
  );
};

export default MessagesReceived;
